import axios from 'axios'
import { Product } from '@libs/types/generated/rates-research-api'

export interface ProductRateResearch extends Product {
  sortValue: number
}

const filterAndModifyProducts = (
  products: ProductRateResearch[]
): ProductRateResearch[] => {
  // Add sort value to the products
  const sortOrder = {
    Fixed: { '30 year': 1, '20 year': 2, '15 year': 3, '10 year': 4 },
    ARM: {
      '3/1': 101,
      '5/1': 102,
      '7/1': 103,
      '10/1': 104,
      '3/6': 105,
      '5/6': 106,
      '7/6': 107,
      '10/6': 108
    },
    Jumbo: { '30': 201, '15': 202 }
  }

  products = products
    .filter(product => {
      return (
        product.vertical !== 'mortgage' ||
        product.dimensions.points_band === 'all'
      )
    })
    .filter(product => {
      // Remove Auto refi products
      if (product.vertical === 'auto' && product.purpose === 'refi') {
        return false
      }

      // Remove Home co-investing product
      if (
        product.vertical === 'home-equity' &&
        product.purpose === 'co-investing'
      ) {
        return false
      }

      // Remove 3/6, 5/6, 7/6, 10/6 refi and jumbo refi products
      if (product.vertical === 'mortgage' && product.purpose === 'refinance') {
        const excludedTerms = ['3/6', '5/6', '7/6', '10/6']
        if (excludedTerms.includes(product.term?.name)) {
          return false
        }
      }

      return true
    })
    .map(product => {
      // Modify Home Equity product descriptions
      if (product.vertical === 'home-equity') {
        // Remove 'fixed' from the description
        product.description = product.description.replace(' fixed', '')

        // Capitalize 'home equity loan'
        const homeEquityLoanRegex = /home equity loan/i
        if (homeEquityLoanRegex.test(product.description)) {
          product.description = product.description.replace(
            homeEquityLoanRegex,
            'Home Equity Loan'
          )
        }
      }

      return product
    })

  products.forEach(product => {
    let productType = product.dimensions.type === 'adjustable' ? 'ARM' : 'Fixed'
    if (product.dimensions.size === 'jumbo') {
      productType = 'Jumbo'
    }

    const term = product.term?.name

    if (sortOrder[productType] && sortOrder[productType][term]) {
      product.sortValue = sortOrder[productType][term]
    } else {
      product.sortValue = 1000 // Default high value for unsorted products
    }
  })

  return products.sort((a, b) => a.sortValue - b.sortValue)
}

const getAll = async (): Promise<Product[]> => {
  const response = await axios.post(
    `${process.env.NEXT_PUBLIC_PRODUCTS_SERVICE_URL}/search`,
    {
      query: {},
      omit_fields: ['attributes', 'market_type', 'locations'],
      disable_cache: false
    }
  )

  return filterAndModifyProducts(response.data.results)
}

const productsApi = {
  getAll
}

export default productsApi
